import { Set as ImmutableSet, List, Map } from 'immutable';
import compare from 'just-compare';
import snakeCase from 'just-snake-case';
import moment from 'moment';
import {
  DesiredLocation,
  NurseOnboardingOptions,
  OnboardingData,
  OnboardingOptions,
  OnboardingUpdates,
  ReactivationOnboardingOptions,
  TechCustomCertification,
  TechOnboardingOptions,
} from 'registration/types';
import {
  TechCertification,
  TechJobDescription,
  TechProfileTechCertification,
  TechProfileTechJobDescription,
} from 'tech/types';

import {
  parseServerWorkExperience,
  WorkExperience,
} from 'shared/components/WorkExperienceControl';
import constants from 'shared/constants/constants.json';
import { shiftOptions } from 'shared/constants/selectOptions';
import {
  getLocationFromCityObj,
  parsePhoneNumber,
} from 'shared/helpers/parsers';
import { Place } from 'shared/records/Place';
import { CareSetting } from 'shared/types';
import Phone from 'shared/types/Phone';
import { TalentProfileType } from 'shared/types/TalentProfile';

import { TalentProfile } from 'talent/types';

import { isDesiredLocationsListOfPlaces } from './desiredLocations';
import parseFullName from './parseFullName';
import { getPhoneText } from './phone';

const dateFormat = 'YYYY-MM-DD';

export const locationFromGooglePlace = (
  place: google.maps.places.AutocompletePrediction
) => ({
  city: place.structured_formatting.main_text,
  id: place.description.replace(', USA', ', United States'),
  label: `${place.structured_formatting.main_text}, ${place.structured_formatting.secondary_text.replace(', USA', '')}`,
  state: place.structured_formatting.secondary_text.replace(', USA', ''),
});

const appendLabelAndValue = (option: CareSetting) => ({
  label: option.display_name,
  value: option.id.toString(),
  ...option,
});

const getStartDateFromOption = (startDateOption: string) => {
  switch (startDateOption) {
    case 'immediately':
      return moment().format(dateFormat);
    case 'few_weeks':
      return moment().add(3, 'weeks').format(dateFormat);
    case 'few_months':
      return moment().add(3, 'months').format(dateFormat);
    default:
      return null;
  }
};

const trueFalseToYesNo = (trueFalseVal: boolean) => {
  if (trueFalseVal === true) {
    return 'yes';
  }
  if (trueFalseVal === false) {
    return 'no';
  }

  return trueFalseVal;
};

const yesNoToTrueFalse = (yesNoVal: string | boolean) => {
  if (typeof yesNoVal === 'boolean') {
    return yesNoVal;
  }

  if (yesNoVal === 'yes') {
    return true;
  }
  if (yesNoVal === 'no') {
    return false;
  }

  if (yesNoVal === '') {
    return false;
  }

  console.error(`Unexpected argument: ${yesNoVal}`);
  return Boolean(yesNoVal);
};

const requiresTalentProfileFromServer = (key: string) => {
  const errorMsg = `convertStateToAttributes -- ${key} requires additional argument: talentProfileFromServer`;
  throw new Error(errorMsg);
};

const getTechProfileData = (
  talentProfileFromServer: Partial<TalentProfile> | Partial<OnboardingData>,
  key: string
) => {
  if (talentProfileFromServer.type === TalentProfileType.TechProfile) {
    return (talentProfileFromServer as Partial<TalentProfile>)[key] || [];
  }
  return [];
};

export const augmentOptions = (
  optionsFromServer:
    | Partial<ReactivationOnboardingOptions>
    | NurseOnboardingOptions
    | TechOnboardingOptions,
  optionsKeys: (
    | keyof ReactivationOnboardingOptions
    | keyof NurseOnboardingOptions
    | keyof TechOnboardingOptions
  )[]
) => {
  const state: Partial<OnboardingData> = {};

  optionsKeys.forEach((key) => {
    switch (key) {
      case 'care_settings': {
        const nurseOptions = optionsFromServer as
          | ReactivationOnboardingOptions
          | NurseOnboardingOptions;
        state.care_settings =
          nurseOptions.care_settings.map(appendLabelAndValue);

        break;
      }

      default:
        state[key] = optionsFromServer[key];
        break;
    }
  });

  return state;
};

export const augmentState = (
  talentProfileFromServer: TalentProfile | Partial<OnboardingData>,
  onboardingOptions: Partial<OnboardingOptions>,
  stateKeys: string[],
  onboardingUpdates?: Partial<OnboardingUpdates>
) => {
  const state: Partial<OnboardingData> = {};
  stateKeys.forEach((key) => {
    switch (key) {
      case 'acute_care_experience': {
        if (talentProfileFromServer.type === TalentProfileType.NurseProfile) {
          state.acute_care_experience = trueFalseToYesNo(
            talentProfileFromServer.acute_care_experience
          );
        }

        break;
      }

      case 'care_settings': {
        if (talentProfileFromServer.type === TalentProfileType.NurseProfile) {
          state.care_settings =
            talentProfileFromServer.care_settings.map(appendLabelAndValue);
        }
        break;
      }

      case 'clinical_care_experience': {
        if (talentProfileFromServer.type === TalentProfileType.NurseProfile) {
          state.clinical_care_experience = trueFalseToYesNo(
            talentProfileFromServer.clinical_care_experience
          );
        }
        break;
      }

      case 'certifications': {
        if (talentProfileFromServer.type === TalentProfileType.NurseProfile) {
          if (!talentProfileFromServer.certifications) {
            state.certifications = [];
          } else {
            state.certifications =
              talentProfileFromServer.certifications.split(', ');
          }
        }
        break;
      }

      case 'other_locations': {
        state.desiredLocations = talentProfileFromServer.other_locations.map(
          (location) => {
            const label = `${location.city}, ${location.state}`;
            const id = `${label}, United States`;
            const zipCode = location.zip_code;
            return {
              id,
              label,
              zipCode,
              ...location,
            };
          }
        );
        break;
      }

      case 'state_licenses': {
        if (talentProfileFromServer.type === TalentProfileType.NurseProfile) {
          state.state_licenses = talentProfileFromServer.state_licenses.map(
            (stateLicense) => {
              const stateObj = onboardingOptions.states.find(
                (stateOption) => stateLicense.state_id === stateOption.id
              );
              return {
                ...stateObj,
                hasActiveLicense: true,
                hasCompactLicense: stateObj.compact
                  ? !!stateLicense.compact
                  : undefined,
              };
            }
          );
        }
        break;
      }

      case 'nurse_specialties_attributes': {
        if (talentProfileFromServer.type === TalentProfileType.NurseProfile) {
          const nurseSpecialtiesAttributes =
            onboardingUpdates?.nurse_specialties_attributes ||
            onboardingUpdates?.nurse_specialties ||
            [];
          const nurseSpecialties = Map<number, number>(
            state.nurseSpecialties as Iterable<[number, number]>
          ).withMutations((specialtiesMap) => {
            nurseSpecialtiesAttributes.forEach((ns) => {
              specialtiesMap.set(
                Number(ns.nurse_specialty_description_id),
                ns.years_experience
              );
            });
          });

          state.nurseSpecialties = nurseSpecialties;
        }
        break;
      }

      case 'user_attributes': {
        const userAttributes = onboardingUpdates?.user_attributes || {};
        state.email =
          userAttributes.email || talentProfileFromServer?.email || '';
        break;
      }

      case 'care_setting_ids': {
        const careSettingIds = onboardingUpdates?.care_setting_ids || [];
        const careSettingOptions =
          // @ts-ignore
          onboardingOptions?.care_settings ||
          // @ts-ignore
          talentProfileFromServer?.care_settings ||
          [];
        if (careSettingOptions) {
          state.care_settings = careSettingOptions
            .filter((cs) => careSettingIds.includes(cs.id))
            .map(appendLabelAndValue);
        }
        break;
      }

      case 'education_level': {
        const educationLevel = onboardingUpdates?.education_level || '';
        if (educationLevel) {
          state.education_level = educationLevel;
          // @ts-ignore
        } else if (talentProfileFromServer?.education_level) {
          // @ts-ignore
          state.education_level = talentProfileFromServer.education_level;
        }
        break;
      }

      case 'recent_grad': {
        if (typeof onboardingUpdates?.recent_grad === 'boolean') {
          state.recent_grad = onboardingUpdates.recent_grad;
        } else {
          state.recent_grad = talentProfileFromServer.recent_grad;
        }
        break;
      }

      case 'work_experiences': {
        const workExperiences =
          onboardingUpdates?.work_experiences_attributes ||
          onboardingUpdates?.work_experiences ||
          [];
        if (workExperiences && workExperiences.length > 0) {
          state.workExperiences = List<WorkExperience>().withMutations(
            (workExperienceList) => {
              workExperiences.forEach((we) => {
                workExperienceList.push(parseServerWorkExperience(we));
              });
            }
          );
        } else if (talentProfileFromServer?.work_experiences) {
          // @ts-ignore
          state.work_experiences = talentProfileFromServer.work_experiences;
        }
        break;
      }

      case 'full_name': {
        const fullName = onboardingUpdates?.full_name || '';
        if (fullName) {
          const [first, last] = parseFullName(fullName);
          state.full_name = fullName;
          state.first_name = first;
          state.last_name = last;
        } else {
          state.full_name = talentProfileFromServer?.full_name || '';
          state.first_name =
            onboardingUpdates?.first_name ||
            talentProfileFromServer?.first_name ||
            '';
          state.last_name =
            onboardingUpdates?.last_name ||
            talentProfileFromServer?.last_name ||
            '';
        }
        break;
      }

      case 'resume_parse_task_id': {
        state.resume_parse_task_id =
          onboardingUpdates?.resume_parse_task_id || null;
        break;
      }

      default:
        state[key] = talentProfileFromServer[key];
        break;
    }
  });

  return state;
};

export const convertToState = (
  talentProfileFromServer: Partial<TalentProfile>,
  stateKeys: string[] | Set<string>
) => {
  const state: Partial<OnboardingData> = {};

  stateKeys.forEach((key) => {
    switch (key) {
      case 'current_zip_code': {
        const {
          current_city: city,
          current_state: stateCode,
          current_zip_code: zipCode,
        } = talentProfileFromServer;

        if (!city || !state) {
          break;
        }
        state.currentLocation = getLocationFromCityObj({
          city,
          state: stateCode,
          zipCode,
        });
        break;
      }

      case 'currentLocation': {
        const {
          current_city: currentCity,
          current_state: currentState,
          current_zip_code: currentZipCode,
        } = talentProfileFromServer;

        state.currentLocation = new Place({
          city: currentCity,
          state: currentState,
          zipCode: currentZipCode,
        });
        break;
      }

      case 'desiredLocations': {
        const desiredLocations = List<Place>().withMutations((locList) => {
          // TODO smarter way to skip other locations for tech profiles?
          const serverOtherLocations =
            talentProfileFromServer.other_locations || [];
          serverOtherLocations.forEach((ol) => {
            const place = new Place({
              city: ol.city,
              state: ol.state,
              zipCode: ol.zip_code,
            });
            locList.push(place);
          });
        });

        state.desiredLocations = desiredLocations;
        break;
      }

      case 'nurseSpecialties': {
        if (talentProfileFromServer.type === TalentProfileType.NurseProfile) {
          const nurseSpecialties = Map<number, number>().withMutations(
            (specialtiesMap) => {
              const serverNurseSpecialties =
                talentProfileFromServer.nurse_specialties || [];
              serverNurseSpecialties.forEach((ns) => {
                specialtiesMap.set(
                  ns.nurse_specialty_description_id,
                  ns.years_experience
                );
              });
            }
          );

          state.nurseSpecialties = nurseSpecialties;
        }
        break;
      }

      case 'shifts': {
        const possibleShifts = shiftOptions.map(
          (shiftOption) => shiftOption.value
        );

        const shifts = ImmutableSet<string>().withMutations((shiftsSet) => {
          possibleShifts.forEach((shift) => {
            if (talentProfileFromServer[shift]) {
              shiftsSet.add(shift);
            }
          });
        });

        state.shifts = shifts;
        break;
      }

      case 'employment_type': {
        const possibleEmploymentTypes = constants.EMPLOYMENT_TYPES;

        const employmentTypes: Array<{ value: string; label: string }> = [];

        possibleEmploymentTypes.forEach((shift) => {
          if (talentProfileFromServer[shift]) {
            const shiftOption = shiftOptions.find((so) => so.value === shift);
            if (shiftOption) {
              employmentTypes.push({ value: shift, label: shiftOption.label });
            }
          }
        });

        state.employment_type = employmentTypes;
        break;
      }

      case 'shift_preferences': {
        const possibleShifts = constants.SHIFTS;

        const shiftPreferences: Array<{ value: string; label: string }> = [];

        possibleShifts.forEach((shift) => {
          if (talentProfileFromServer[shift]) {
            const shiftOption = shiftOptions.find((so) => so.value === shift);
            if (shiftOption) {
              shiftPreferences.push({ value: shift, label: shiftOption.label });
            }
          }
        });

        state.shift_preferences = shiftPreferences;
        break;
      }

      case 'careSettings': {
        if (talentProfileFromServer.type === TalentProfileType.NurseProfile) {
          state.careSettings = List(talentProfileFromServer.care_settings);
        }
        break;
      }

      case 'startDateOption': {
        const { start_date: startDate, start_date_option: startDateOption } =
          talentProfileFromServer;

        let stateOption;
        if (startDateOption) {
          stateOption = startDateOption;
        } else if (startDate) {
          const mDate = moment(startDate);
          const immediately = moment();
          const fewWeeks = moment().add(3, 'weeks');

          if (moment.max(mDate, immediately) === immediately) {
            stateOption = 'immediately';
          } else if (moment.max(mDate, fewWeeks) === fewWeeks) {
            stateOption = 'few_weeks';
          } else {
            stateOption = 'few_months';
          }
        } else {
          stateOption = '';
        }
        state.startDateOption = stateOption;
        break;
      }

      case 'stateLicenses': {
        if (talentProfileFromServer.type === TalentProfileType.NurseProfile) {
          const stateLicenses = ImmutableSet<number>().withMutations(
            (stateLicenseSet) => {
              const serverLicenses =
                talentProfileFromServer.state_licenses || [];
              serverLicenses.forEach((sl) => {
                stateLicenseSet.add(sl.state_id);
              });
            }
          );

          state.stateLicenses = stateLicenses;
        }
        break;
      }

      case 'workExperiences': {
        const workExperiences = List<WorkExperience>().withMutations(
          (workExperienceList) => {
            const serverWorkExperiences =
              talentProfileFromServer.work_experiences || [];
            serverWorkExperiences.forEach((we) => {
              workExperienceList.push(parseServerWorkExperience(we));
            });
          }
        );

        if (workExperiences.isEmpty()) {
          state.workExperiences = List([new WorkExperience()]);
        } else {
          state.workExperiences = workExperiences;
        }

        break;
      }

      case 'phone': {
        const phone: Partial<Phone> = talentProfileFromServer.phone || {};

        state.phone = phone.tel_uri || '';
        break;
      }

      case 'techRoleOptions': {
        if (talentProfileFromServer.type === TalentProfileType.TechProfile) {
          const techRoleOptions = [];

          talentProfileFromServer.tech_job_descriptions.forEach((tjd) => {
            techRoleOptions.push({
              id: tjd.id,
              title: tjd.title,
              category: tjd.category,
              tech_job_description_profile_id:
                tjd.tech_job_description_profile_id,
            });
          });

          state.techRoleOptions = techRoleOptions;
        }
        break;
      }

      case 'techCertifications': {
        if (talentProfileFromServer.type === TalentProfileType.TechProfile) {
          const techCertifications = [];

          talentProfileFromServer.tech_certifications.forEach((tc) => {
            techCertifications.push(tc.tech_certification_description_id);
          });

          state.techCertifications = techCertifications;
        }
        break;
      }

      case 'techCustomCertifications': {
        if (talentProfileFromServer.type === TalentProfileType.TechProfile) {
          const techCustomCertifications = [];
          talentProfileFromServer.tech_custom_certifications.forEach((tc) => {
            techCustomCertifications.push(tc);
          });

          state.techCustomCertifications = techCustomCertifications;
        }
        break;
      }

      case 'talentEducationExperiences': {
        if (talentProfileFromServer.type === TalentProfileType.TechProfile) {
          const talentEducationExperiences = [];

          talentProfileFromServer.talent_education_experiences.forEach((te) => {
            talentEducationExperiences.push(te);
          });

          state.talentEducationExperiences = talentEducationExperiences;
        }
        break;
      }

      default: {
        const value = talentProfileFromServer[snakeCase(key)];
        state[key] = value == null ? '' : value;
        break;
      }
    }
  });

  return state;
};

export const convertStateToAttributes = (
  stateTalentProfile: Partial<OnboardingData>,
  stateKeys: string[],
  talentProfileFromServer:
    | Partial<TalentProfile>
    | Partial<OnboardingData> = null
) => {
  const talentProfileAttributes: OnboardingUpdates = {};

  stateKeys.forEach((key) => {
    switch (key) {
      case 'acute_care_experience': {
        talentProfileAttributes.acute_care_experience = yesNoToTrueFalse(
          stateTalentProfile.acute_care_experience
        );
        break;
      }

      case 'certifications': {
        if (Array.isArray(stateTalentProfile.certifications)) {
          talentProfileAttributes.certifications =
            stateTalentProfile.certifications.join(', ');
        } else {
          talentProfileAttributes.certifications =
            stateTalentProfile.certifications;
        }
        break;
      }

      case 'clinical_care_experience': {
        talentProfileAttributes.clinical_care_experience = yesNoToTrueFalse(
          stateTalentProfile.clinical_care_experience
        );
        break;
      }

      case 'currentLocation': {
        /* eslint-disable @typescript-eslint/naming-convention */
        const { city, state, zipCode, zip_code } =
          stateTalentProfile.currentLocation;

        talentProfileAttributes.current_city = city;
        talentProfileAttributes.current_state = state;
        talentProfileAttributes.current_zip_code = zipCode || zip_code;
        break;
      }

      case 'desiredLocations': {
        if (!stateTalentProfile.desiredLocations) break;
        let otherLocations;
        if (
          isDesiredLocationsListOfPlaces(stateTalentProfile.desiredLocations)
        ) {
          otherLocations = stateTalentProfile.desiredLocations
            .filterNot((place) => place.isEmpty())
            .map((place) => ({
              city: place.city,
              state: place.state,
              zip_code: place.zipCode,
            }))
            .toArray();
        } else {
          const expandedLocations = [];
          otherLocations = stateTalentProfile.desiredLocations
            .map((place) => {
              if (place.city) {
                return {
                  city: place.city,
                  state: place.state,
                  zip_code: place.zipCode,
                };
              }

              if (place.hospitalLocations) {
                return place.hospitalLocations
                  .split(';')
                  .map((hospitalLocation) => {
                    const [city, state, zipCode] = hospitalLocation.split(',');
                    return { city, state, zip_code: zipCode };
                  });
              }
              return null;
            })
            .filter((place) => place !== null)
            .flatMap((place) => place);

          if (expandedLocations.length) {
            otherLocations = otherLocations.concat(expandedLocations);
          }
        }

        talentProfileAttributes.other_locations_attributes = otherLocations;
        break;
      }

      case 'email': {
        const userAttributes = talentProfileAttributes.user_attributes || {};
        userAttributes.email = stateTalentProfile.email;
        talentProfileAttributes.user_attributes = userAttributes;
        break;
      }

      case 'fullName': {
        const [first, last] = parseFullName(stateTalentProfile.fullName);
        talentProfileAttributes.first_name = first;
        talentProfileAttributes.last_name = last;
        break;
      }

      case 'nurse_specialties': {
        const { nurse_specialties } = stateTalentProfile;
        talentProfileAttributes.nurse_specialties_attributes =
          nurse_specialties;
        break;
      }

      case 'nurseSpecialties': {
        if (!talentProfileFromServer) {
          const errorMsg =
            'convertStateToAttributes -- nurseSpecialties ' +
            'requires additional argument: talentProfileFromServer';
          throw new Error(errorMsg);
        }

        const { nurseSpecialties } = stateTalentProfile;
        const nurseSpecialtiesFromServer =
          talentProfileFromServer.type === TalentProfileType.NurseProfile
            ? talentProfileFromServer.nurse_specialties || []
            : [];

        const specialtiesAttributes = [];
        nurseSpecialtiesFromServer.forEach((specialtyFromServer) => {
          const serverSdId = specialtyFromServer.nurse_specialty_description_id;

          if (nurseSpecialties.has(serverSdId)) {
            const yearsExperience = nurseSpecialties.get(serverSdId);
            specialtiesAttributes.push({
              ...specialtyFromServer,
              years_experience: yearsExperience,
            });
          } else {
            specialtiesAttributes.push({
              ...specialtyFromServer,
              _destroy: true,
            });
          }
        });

        nurseSpecialties.forEach((yrsExp, nurseSpecialtyDescriptionId) => {
          const existingSpecialty = nurseSpecialtiesFromServer.find(
            (specialty) =>
              specialty.nurse_specialty_description_id ===
              nurseSpecialtyDescriptionId
          );
          if (!existingSpecialty) {
            specialtiesAttributes.push({
              nurse_specialty_description_id: nurseSpecialtyDescriptionId,
              years_experience: yrsExp,
            });
          }
        });

        talentProfileAttributes.nurse_specialties_attributes =
          specialtiesAttributes;
        break;
      }

      case 'password': {
        const userAttributes = talentProfileAttributes.user_attributes || {};
        userAttributes.password = stateTalentProfile.password;
        userAttributes.password_confirmation = stateTalentProfile.password;
        talentProfileAttributes.user_attributes = userAttributes;
        break;
      }

      case 'shifts': {
        if (typeof stateTalentProfile.shifts === 'string') {
          talentProfileAttributes.shifts = stateTalentProfile.shifts;
        } else {
          const possibleShifts = shiftOptions.map(
            (shiftOption) => shiftOption.value
          );
          const { shifts } = stateTalentProfile;
          possibleShifts.forEach((shift) => {
            talentProfileAttributes[shift] = shifts.has(shift);
          });
        }
        break;
      }

      case 'careSettings': {
        const { careSettings } = stateTalentProfile;

        talentProfileAttributes.care_settings = careSettings.toArray();
        talentProfileAttributes.care_setting_ids = careSettings
          .map((cs) => cs.id)
          .toArray();

        break;
      }

      case 'care_settings': {
        /* eslint-disable @typescript-eslint/naming-convention */
        const { care_settings } = stateTalentProfile;

        talentProfileAttributes.care_setting_ids = care_settings.map(
          (cs) => cs.id
        );

        break;
      }

      case 'startDateOption': {
        const { startDateOption } = stateTalentProfile;
        const startDate = getStartDateFromOption(startDateOption);

        talentProfileAttributes.start_date_option = startDateOption;
        talentProfileAttributes.start_date = startDate;
        break;
      }

      case 'start_date': {
        const { start_date: startDate } = stateTalentProfile;
        if (startDate instanceof Date) {
          talentProfileAttributes.start_date =
            moment(startDate).format(dateFormat);
        } else {
          talentProfileAttributes.start_date = startDate;
        }
        break;
      }

      case 'start_date_option': {
        const { start_date: startDate, start_date_option: startDateOption } =
          stateTalentProfile;
        talentProfileAttributes.start_date_option = startDateOption;
        if (startDate && startDateOption === 'specific_date') {
          talentProfileAttributes.start_date_option = null;
        } else {
          talentProfileAttributes.start_date =
            getStartDateFromOption(startDateOption);
        }
        break;
      }

      case 'employment_type': {
        if (!talentProfileFromServer) {
          requiresTalentProfileFromServer('employment_type');
        }

        const employmentTypeValues = stateTalentProfile.employment_type.map(
          (employment_type) => employment_type.value
        );

        constants.EMPLOYMENT_TYPES.forEach((employment_type) => {
          if (employmentTypeValues.includes(employment_type)) {
            talentProfileAttributes[employment_type] = true;
          } else if (talentProfileFromServer[employment_type]) {
            talentProfileAttributes[employment_type] = null;
          }
        });

        break;
      }

      case 'shift_preferences': {
        if (!talentProfileFromServer) {
          requiresTalentProfileFromServer('shift_preferences');
        }

        const shiftPreferencesValues = stateTalentProfile.shift_preferences.map(
          (shift_preference) => shift_preference.value
        );

        constants.SHIFTS.forEach((shift) => {
          if (shiftPreferencesValues.includes(shift)) {
            talentProfileAttributes[shift] = true;
          } else if (talentProfileFromServer[shift]) {
            talentProfileAttributes[shift] = false;
          }
        });

        break;
      }

      case 'workExperiences': {
        if (stateTalentProfile.workExperienceMethod === 'manual_entry') {
          const { workExperiences } = stateTalentProfile;

          const workExperienceAttributes = workExperiences
            .filterNot((we) => we.isEmpty())
            .map((we) => ({
              id: we.id,
              _destroy: we._destroy,
              company_name: we.companyName,
              location: we.location,
              job_title: we.jobTitle,
              start_date: we.getStartDate(),
              end_date: we.getEndDate(),
              duties: we.duties,
              work_experience_specialties_attributes:
                we.workExperienceSpecialties.toJS().map((wes) => ({
                  id: wes.id,
                  _destroy: wes._destroy,
                  nurse_specialty_description_id:
                    wes.nurse_specialty_description_id,
                  work_experience_specialty_skills_attributes:
                    wes.work_experience_specialty_skills.map((wess) => ({
                      id: wess.id,
                      _destroy: wess._destroy,
                      nurse_specialty_skill_description_id:
                        wess.nurse_specialty_skill_description_id,
                    })),
                })),
            }))
            .toArray();

          talentProfileAttributes.work_experiences_attributes =
            workExperienceAttributes;
        }
        break;
      }

      case 'resumeFilename':
      case 'resumeFormData':
        if (stateTalentProfile.workExperienceMethod === 'upload') {
          talentProfileAttributes[snakeCase(key)] = stateTalentProfile[key];
        }
        break;

      case 'stateLicenses': {
        talentProfileAttributes.state_licenses_attributes =
          stateTalentProfile.stateLicenses
            .map((state) => {
              if (['string', 'number'].includes(typeof state)) {
                return { state_id: state };
              }
              return state;
            })
            .toArray();
        break;
      }

      case 'phone': {
        const { phone, disallow_duplicate_phone } = stateTalentProfile;
        if (typeof phone !== 'undefined') {
          talentProfileAttributes.phone = parsePhoneNumber(getPhoneText(phone));
        }

        if (disallow_duplicate_phone) {
          talentProfileAttributes.disallow_duplicate_phone =
            disallow_duplicate_phone;
        }

        break;
      }

      case 'sms_opt_in': {
        talentProfileAttributes.sms_opt_in =
          stateTalentProfile.sms_opt_in === true;
        break;
      }

      case 'license_issue_date': {
        const { license_issue_date: licenseIssueDate } = stateTalentProfile;
        if (licenseIssueDate instanceof Date) {
          talentProfileAttributes.license_issue_date =
            moment(licenseIssueDate).format(dateFormat);
        } else {
          talentProfileAttributes.license_issue_date = licenseIssueDate;
        }
        break;
      }

      case 'graduation_date': {
        const { graduation_date: graduationDate } = stateTalentProfile;
        if (graduationDate instanceof Date) {
          talentProfileAttributes.graduation_date =
            moment(graduationDate).format(dateFormat);
        } else {
          talentProfileAttributes.graduation_date = graduationDate;
        }
        break;
      }

      case 'techRoleOptions': {
        if (!talentProfileFromServer) {
          requiresTalentProfileFromServer('techRoleOptions');
        }

        const techRoleOptionsFromServer = getTechProfileData(
          talentProfileFromServer,
          'tech_job_descriptions'
        );

        const stateRoleIds = new Set<number>(
          stateTalentProfile.techRoleOptions.map((role) => role.id)
        );

        const serverRolesMap = Map<
          number,
          TechProfileTechJobDescription & TechJobDescription
        >(techRoleOptionsFromServer.map((role) => [role.id, role]));

        const techRoleOptionsAttributes = [];

        serverRolesMap.keySeq().forEach((serverRoleId) => {
          if (!stateRoleIds.has(serverRoleId)) {
            const roleFromServer = serverRolesMap.get(serverRoleId);
            techRoleOptionsAttributes.push({
              id: roleFromServer.tech_job_description_profile_id,
              _destroy: true,
            });
          }
        });

        stateRoleIds.forEach((roleId) => {
          if (!serverRolesMap.has(roleId)) {
            techRoleOptionsAttributes.push({
              tech_job_description_id: roleId,
            });
          }
        });

        talentProfileAttributes.tech_job_descriptions_profiles_attributes =
          techRoleOptionsAttributes;
        break;
      }

      case 'techCertifications': {
        if (!talentProfileFromServer) {
          requiresTalentProfileFromServer('techCertifications');
        }

        const { techCertifications } = stateTalentProfile;
        const techCertificationsFromServer = getTechProfileData(
          talentProfileFromServer,
          'tech_certifications'
        );

        const stateCertIds = new Set<number>(techCertifications);

        const serverCertsMap = Map<
          number,
          TechProfileTechCertification & TechCertification
        >(
          techCertificationsFromServer.map((cert) => [
            cert.tech_certification_description_id,
            cert,
          ])
        );

        const techCertificationsAttributes = [];

        serverCertsMap.keySeq().forEach((serverCertId) => {
          if (!stateCertIds.has(serverCertId)) {
            const certFromServer = serverCertsMap.get(serverCertId);
            techCertificationsAttributes.push({
              id: certFromServer.id,
              _destroy: true,
            });
          }
        });

        stateCertIds.forEach((certId) => {
          if (!serverCertsMap.has(certId)) {
            techCertificationsAttributes.push({
              tech_certification_description_id: certId,
            });
          }
        });

        talentProfileAttributes.tech_certifications_attributes =
          techCertificationsAttributes;
        break;
      }

      case 'techCustomCertifications': {
        if (!talentProfileFromServer) {
          requiresTalentProfileFromServer('techCustomCertifications');
        }

        const { techCustomCertifications } = stateTalentProfile;
        const techCustomCertificationsFromServer = getTechProfileData(
          talentProfileFromServer,
          'tech_custom_certifications'
        );

        const stateCertIds = new Set<number>(
          techCustomCertifications.map((cert) => cert.id).filter(Boolean)
        );

        const serverCertsMap = Map<number, TechCustomCertification>(
          techCustomCertificationsFromServer.map((cert) => [cert.id, cert])
        );

        const techCustomCertificationsAttributes = [];

        serverCertsMap.keySeq().forEach((serverCertId) => {
          if (!stateCertIds.has(serverCertId)) {
            techCustomCertificationsAttributes.push({
              id: serverCertId,
              _destroy: true,
            });
          }
        });

        techCustomCertifications.forEach((certification) => {
          const payload = {
            description: certification.description,
          };

          if (!certification.id || !serverCertsMap.has(certification.id)) {
            techCustomCertificationsAttributes.push(payload);
          } else {
            techCustomCertificationsAttributes.push({
              id: certification.id,
              ...payload,
            });
          }
        });

        talentProfileAttributes.tech_custom_certifications_attributes =
          techCustomCertificationsAttributes;
        break;
      }

      case 'talentEducationExperiences': {
        if (!talentProfileFromServer) {
          requiresTalentProfileFromServer('talentEducationExperiences');
        }
        const { talentEducationExperiences } = stateTalentProfile;
        const talentEducationExperiencesFromServer = getTechProfileData(
          talentProfileFromServer,
          'talent_education_experiences'
        );

        const stateEducationIds = new Set<number>(
          talentEducationExperiences.map((edu) => edu.id).filter(Boolean)
        );

        const serverEducationsMap = Map<number, any>(
          talentEducationExperiencesFromServer.map((edu) => [edu.id, edu])
        );

        const talentEducationExperiencesAttributes = [];

        serverEducationsMap.keySeq().forEach((serverEducationId) => {
          if (!stateEducationIds.has(serverEducationId)) {
            talentEducationExperiencesAttributes.push({
              id: serverEducationId,
              _destroy: true,
            });
          }
        });

        talentEducationExperiences.forEach((education) => {
          const payload = {
            degree_type: education.degree_type,
            institution_name: education.institution_name,
            major: education.major,
            graduation_date: `${education.graduation_year}-${education.graduation_month}-01`,
            tech_certification_program: education.tech_certification_program,
          };

          if (!education.id || !serverEducationsMap.has(education.id)) {
            talentEducationExperiencesAttributes.push(payload);
          } else {
            talentEducationExperiencesAttributes.push({
              id: education.id,
              ...payload,
            });
          }
        });
        talentProfileAttributes.talent_education_experiences_attributes =
          talentEducationExperiencesAttributes;
        break;
      }

      default:
        talentProfileAttributes[snakeCase(key)] = stateTalentProfile[key];
        break;
    }
  });

  return talentProfileAttributes;
};

export const getAugmentedOnboardingData = (
  onboardingData: OnboardingData,
  talentProfile: TalentProfile,
  onboardingOptions: Partial<OnboardingOptions>,
  onboardingUpdates?: Partial<OnboardingUpdates>
) => {
  const profileKeysToConvert = Object.keys(talentProfile).filter(
    (key) => !['phone'].includes(key)
  );
  profileKeysToConvert.push('currentLocation');
  // TODO: It is safer to just hack this and set for techs.
  profileKeysToConvert.push('nurseSpecialties');
  profileKeysToConvert.push('workExperiences');
  profileKeysToConvert.push('techRoleOptions');
  profileKeysToConvert.push('employment_type');
  profileKeysToConvert.push('shift_preferences');
  profileKeysToConvert.push('talentEducationExperiences');
  profileKeysToConvert.push('techCertifications');
  return {
    ...onboardingData,
    ...{
      ...augmentState(
        convertToState(talentProfile, profileKeysToConvert),
        onboardingOptions,
        [
          ...profileKeysToConvert,
          'nurse_specialties_attributes',
          'user_attributes',
          'care_setting_ids',
          'education_level',
          'recent_grad',
          'work_experiences',
          'resume_parse_task_id',
        ],
        onboardingUpdates
      ),
      phone: talentProfile.phone,
    },
  };
};

export const getTalentAttribute = (
  attributeCamelCase: string,
  localTalentProfile: OnboardingData,
  talentProfileFromServer: TalentProfile,
  favorValuesFromServer = true
) => {
  const attributeSnakeCase = snakeCase(attributeCamelCase);
  const hasLocalProfile =
    localTalentProfile && Object.keys(localTalentProfile).length;
  const hasServerProfile =
    talentProfileFromServer && Object.keys(talentProfileFromServer).length;
  const localAttrValue =
    hasLocalProfile && localTalentProfile[attributeCamelCase];
  const serverAttrValue =
    hasServerProfile && talentProfileFromServer[attributeSnakeCase];

  if (favorValuesFromServer) {
    return serverAttrValue || localAttrValue;
  }

  return localAttrValue || serverAttrValue;
};

export const talentProfileAttributesWouldUpdateServer = (
  talentProfileAttributes: Partial<OnboardingUpdates>,
  talentProfileFromServer: Partial<TalentProfile>
) =>
  Object.entries(talentProfileAttributes).some((entry) => {
    const [key, value] = entry;
    let localState = value;
    let serverState = talentProfileFromServer[key];
    switch (key) {
      case 'nurse_specialties_attributes': {
        if (talentProfileFromServer.type === TalentProfileType.NurseProfile) {
          localState = (
            value as OnboardingUpdates['nurse_specialties_attributes']
          ).map((ns) => ({
            nurse_specialty_description_id: ns.nurse_specialty_description_id,
            years_experience: ns.years_experience,
          }));
          serverState = talentProfileFromServer.nurse_specialties.map((ns) => ({
            nurse_specialty_description_id: ns.nurse_specialty_description_id,
            years_experience: ns.years_experience,
          }));
        }
        break;
      }
      case 'state_licenses_attributes': {
        if (talentProfileFromServer.type === TalentProfileType.NurseProfile) {
          localState = (
            value as OnboardingUpdates['state_licenses_attributes']
          ).map((sl) => sl.state_id);
          serverState = talentProfileFromServer.state_licenses.map(
            (sl) => sl.state_id
          );
        }
        break;
      }
      case 'tech_job_descriptions_profiles_attributes': {
        if (talentProfileFromServer.type === TalentProfileType.TechProfile) {
          localState = (
            value as OnboardingUpdates['tech_job_descriptions_profiles_attributes']
          ).map((tjd) => tjd.id);
          serverState = talentProfileFromServer.tech_job_descriptions.map(
            (tjd) => tjd.id
          );
        }
        break;
      }

      default:
    }

    return !compare(localState, serverState);
  });
